import { FieldTitle } from './RedStarTitle';
import Select from 'react-select';
import { Controller, useFormContext } from 'react-hook-form';
import { FieldDesc } from './common';
import { TextField } from '@client/new/components/basic';
import { TwoColumns } from './layouts';
import { CTAInput } from './CTAInput';
import { LandingMediaDropbox } from './MediaDropBox';
import { TogglableButtons } from '@design-system/components/togglableButtons';
import { OfferData } from './types';
import { faImage } from '@fortawesome/pro-regular-svg-icons';
import { useEffect } from 'react';

type Props = {
  index: number;
  offers?: OfferData[];
  imageUpload: (file: File) => Promise<string>;
};

// Helper to format price in cents to dollar string
const formatPrice = (priceInCents?: number): string => {
  if (!priceInCents) return '$XX.XX';
  const dollars = (priceInCents / 100).toFixed(2);
  return `$${dollars}`;
};

export function FeaturedOffer({ index, offers = [], imageUpload }: Props) {
  const { register, control, watch, setValue } = useFormContext();
  const r = (s: string) => register(`sections.${index}.${s}`);
  const state = watch(`sections.${index}`);

  // Prepare options for the offer dropdown
  const offerOptions = offers.map((offer) => ({
    value: offer.id,
    label: offer.name,
  }));

  // Get selected offer data
  const selectedOffer = offers.find((o) => o.id === state.offerId);

  // Get listed items options from the selected offer
  const listedItemsOptions = selectedOffer?.listedItems?.options || [];

  // Calculate dynamic default values for CTAs
  const cta1DefaultText = selectedOffer?.price
    ? `Buy for ${formatPrice(selectedOffer.price)}`
    : 'Buy for $XX.XX';
  const cta1DefaultLink = selectedOffer?.checkoutUrl || '';

  const cta2DefaultText = 'Learn More';
  const cta2DefaultLink = selectedOffer?.landingPageUrl || '';

  // Auto-fill headlineText and description when offer is selected (only if fields are empty)
  useEffect(() => {
    if (!selectedOffer) return;

    const currentHeadline = state.headlineText;
    const currentDescription = state.description;

    // Only auto-fill if fields are empty
    if (!currentHeadline || currentHeadline.trim() === '') {
      setValue(`sections.${index}.headlineText`, selectedOffer.name);
    }

    if (
      selectedOffer.description &&
      (!currentDescription || currentDescription.trim() === '')
    ) {
      setValue(`sections.${index}.description`, selectedOffer.description);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.offerId]); // Only trigger on offer change

  // Reset handlers
  const handleResetCTA1 = () => {
    setValue(`sections.${index}.cta1.enabled`, true);
    setValue(`sections.${index}.cta1.text`, '');
    setValue(`sections.${index}.cta1.link`, '');
  };

  const handleResetCTA2 = () => {
    setValue(`sections.${index}.cta2.enabled`, true);
    setValue(`sections.${index}.cta2.text`, '');
    setValue(`sections.${index}.cta2.link`, '');
  };

  // First column: Offer selection, Headline, Description, Listed items
  const firstCol = (
    <>
      <div>
        <FieldTitle title="Choose offer" />
        <FieldDesc>
          Select a single offer to highlight in this section.
        </FieldDesc>
        <Controller
          name={`sections.${index}.offerId`}
          control={control}
          render={({
            field: { value, onChange, ref },
            fieldState: { error },
          }) => (
            <>
              <Select
                ref={ref}
                options={offerOptions}
                value={offerOptions.find((o) => o.value === value) || null}
                onChange={(option) => onChange(option?.value || null)}
                placeholder="Select offer..."
                styles={{
                  control: (provided) => ({
                    ...provided,
                    minHeight: '56px',
                    borderColor: error ? '#d32f2f' : provided.borderColor,
                  }),
                }}
              />
              {error && (
                <div className="text-red-600 text-sm mt-1">{error.message}</div>
              )}
            </>
          )}
        />
      </div>

      <div className="mt-6">
        <FieldTitle title="Headline text" />
        <FieldDesc>
          The headline is used to communicate the offer&apos;s value
          proposition.
        </FieldDesc>
        <TextField
          {...r('headlineText')}
          className="mb-[5px] max-w-[680px] w-full"
          inputProps={{
            placeholder: 'Headline text',
            maxLength: 60,
          }}
        />
      </div>

      <div className="mt-6">
        <FieldTitle title="Description" optional />
        <FieldDesc>
          Additional description text to explain the offer details.
        </FieldDesc>
        <TextField
          {...r('description')}
          className="mb-[5px] max-w-[680px] w-full"
          multiline
          inputProps={{
            placeholder: 'Describe your offer...',
            maxLength: 500,
          }}
        />
      </div>

      {selectedOffer && listedItemsOptions.length > 0 && (
        <div className="mt-6">
          <FieldTitle title="Listed items" optional />
          <Controller
            name={`sections.${index}.listedItems`}
            control={control}
            render={({ field: { value, onChange } }) => (
              <TogglableButtons
                options={listedItemsOptions.map((option) => ({
                  value: option.value,
                  label: option.label,
                }))}
                value={value || []}
                onChange={onChange}
              />
            )}
          />
        </div>
      )}
    </>
  );

  // Second column: Image, CTAs, Swap sides
  const secondCol = (
    <>
      <div>
        <Controller
          name={`sections.${index}.coverImage`}
          control={control}
          render={({
            field: { value, onChange, ref },
            fieldState: { error },
          }) => (
            <LandingMediaDropbox
              inputRef={ref}
              width="100%"
              height="300px"
              upload={imageUpload}
              label="Cover Image"
              validationText="Upload an image for the featured offer section."
              value={value}
              onChange={onChange}
              type="image"
              icon={faImage as any}
              errorMsg={error?.message}
            ></LandingMediaDropbox>
          )}
        />
      </div>

      <CTAInput
        index={index}
        ctaIndex={1}
        label="Checkout Call To Action"
        defaultText={cta1DefaultText}
        defaultLink={cta1DefaultLink}
        onReset={handleResetCTA1}
      />
      <CTAInput
        index={index}
        ctaIndex={2}
        label="Offer's Landing Page Call To Action"
        defaultText={cta2DefaultText}
        defaultLink={cta2DefaultLink}
        onReset={handleResetCTA2}
      />
    </>
  );

  return <TwoColumns first={firstCol} second={secondCol} />;
}
